/**
 * /admin/catalogos/tags — CRUD do catálogo de tags (FEAT-V2-031).
 *
 * Admin gerencia tags globais ou restritas a workflows específicos. UI:
 *   - Header: toggle "Mostrar inativas" + botão "Nova tag"
 *   - Lista de tags com preview do badge + nome + escopo (Global/Workflow X)
 *     + contagem de uso + botão editar + botão excluir (soft)
 *   - Form em Dialog: nome, cor (TagColorPicker), ícone, descricao,
 *     workflows_permitidos (multi-select)
 *
 * Decisão FEAT-V2-031: contagem de uso vem do GET /:id/detail — pra não
 * pesar a listagem com COUNT() por linha, mostra "—" inicial e busca
 * lazy quando admin clica em editar.
 */

import { useState, useMemo } from 'react';

import { Trash2 } from 'lucide-react';

import {
  Button,
  Card,
  Input,
  Label,
  PageHeader,
  Switch,
  Alert,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  EmptyState,
  useConfirmDialog,
} from '@/components/ui';
// BUG-V2-054: TagIconPicker substitui input text livre pro ícone.
import { TagBadge, TagColorPicker, TagIconPicker } from '@/components/tags';
import {
  useTagsCatalog,
  useTagDetail,
  useTagsCatalogActions,
  type Tag,
  type CreateTagPayload,
} from '@/lib/tags-store';
import { useWorkflows } from '@/lib/workflows-store';
import { useAuth } from '@/contexts/AuthContext';

const DEFAULT_COLOR = '#94a3b8';

export function TagsCatalogPage() {
  const { user } = useAuth();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);
  const [creating, setCreating] = useState(false);
  const { data: tags = [], isLoading } = useTagsCatalog(null, { includeInactive });

  if (!user?.isAdmin) {
    return (
      <Alert variant="danger" rail>
        Esta página é restrita a administradores.
      </Alert>
    );
  }

  return (
    <div className="min-w-0">
      <PageHeader
        crumb="Configurações · Cadastros"
        title="Tags"
        subtitle="Etiquetas que você aplica em negócios, ordens de serviço e tarefas para agrupar e filtrar."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2">
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                data-testid="tags-include-inactive"
              />
              Mostrar inativas
            </label>
            <Button
              variant="primary"
              data-testid="tags-create"
              onClick={() => setCreating(true)}
            >
              + Nova tag
            </Button>
          </>
        }
      />

      <Card>
        {isLoading ? (
          <p className="p-4 text-sm text-ink-3 dark:text-ink-dark-3 italic">Carregando…</p>
        ) : tags.length === 0 ? (
          <EmptyState
            title="Nenhuma tag cadastrada"
            description='Clique em "+ Nova tag" pra criar a primeira.'
          />
        ) : (
          <ul className="divide-y divide-line dark:divide-line-dark">
            {tags.map((t) => (
              <TagRow key={t.id} tag={t} onEdit={() => setEditing(t)} />
            ))}
          </ul>
        )}
      </Card>

      {(creating || editing) && (
        <TagFormDialog
          tag={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function TagRow({ tag, onEdit }: { tag: Tag; onEdit: () => void }) {
  const { softDelete } = useTagsCatalogActions();
  const confirm = useConfirmDialog();
  const escopo = tag.workflowsPermitidos
    ? `Só nos fluxos: ${tag.workflowsPermitidos.join(', ')}`
    : 'Vale para todos os fluxos';

  async function handleDelete() {
    const ok = await confirm({
      title: 'Desativar tag',
      message: `A tag "${tag.nome}" fica inativa. Onde ela já foi usada continua aparecendo, mas ela some da lista de tags para adicionar.`,
      confirmLabel: 'Desativar',
      variant: 'destructive',
    });
    if (!ok) return;
    softDelete.mutate(tag.id);
  }

  return (
    <li
      data-testid={`tag-row-${tag.id}`}
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <TagBadge tag={tag} size="md" />
        <span className="min-w-0 break-words text-xs text-ink-3 dark:text-ink-dark-3">
          {escopo}
        </span>
        {!tag.ativo && (
          <span className="text-xs text-ink-3 dark:text-ink-dark-3 italic">(inativa)</span>
        )}
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          data-testid={`tag-row-edit-${tag.id}`}
        >
          Editar
        </Button>
        {tag.ativo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            aria-label={`Desativar tag ${tag.nome}`}
            title="Desativar tag"
            data-testid={`tag-row-delete-${tag.id}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
    </li>
  );
}

function TagFormDialog({ tag, onClose }: { tag: Tag | null; onClose: () => void }) {
  const isEditing = !!tag;
  const { data: workflows = [] } = useWorkflows({ ativo: true });
  const { create, update } = useTagsCatalogActions();
  const { data: detail } = useTagDetail(tag?.id ?? null);

  const [nome, setNome] = useState(tag?.nome ?? '');
  const [cor, setCor] = useState(tag?.cor ?? DEFAULT_COLOR);
  const [icone, setIcone] = useState(tag?.icone ?? '');
  const [descricao, setDescricao] = useState(tag?.descricao ?? '');
  const [escopoGlobal, setEscopoGlobal] = useState(
    tag?.workflowsPermitidos === null || !tag,
  );
  const [workflowsPermitidos, setWorkflowsPermitidos] = useState<string[]>(
    tag?.workflowsPermitidos ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const usageCount = useMemo(() => detail?.usageCount ?? 0, [detail]);

  async function handleSubmit() {
    const trimmed = nome.trim();
    if (!trimmed) {
      setError('Nome obrigatório');
      return;
    }
    if (!escopoGlobal && workflowsPermitidos.length === 0) {
      setError('Escolha pelo menos um fluxo de trabalho — ou deixe a tag valendo para todos.');
      return;
    }
    setError(null);
    const payload: CreateTagPayload = {
      nome: trimmed,
      cor,
      icone: icone.trim() || null,
      descricao: descricao.trim() || null,
      workflowsPermitidos: escopoGlobal ? null : workflowsPermitidos,
    };
    try {
      if (isEditing) {
        await update.mutateAsync({ id: tag!.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      const e = err as { response?: { error?: string; nome?: string } };
      if (e?.response?.error === 'tag-name-exists') {
        setError(`Já existe uma tag chamada "${e.response.nome ?? trimmed}".`);
      } else {
        setError('Não foi possível salvar a tag. Tente de novo em instantes.');
      }
    }
  }

  function toggleWorkflow(slug: string) {
    setWorkflowsPermitidos((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>{isEditing ? 'Editar tag' : 'Nova tag'}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          <div>
            <Label htmlFor="tag-form-nome">Nome</Label>
            <Input
              id="tag-form-nome"
              data-testid="tag-form-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={30}
              autoFocus
            />
          </div>
          <div>
            <Label>Cor</Label>
            <TagColorPicker value={cor} onChange={setCor} />
          </div>
          <div>
            <Label>Ícone (opcional)</Label>
            {/* BUG-V2-054: picker visual em vez de input text. Lista curada
                de ~40 emojis úteis pra tags. Compat retroativa: tags com
                emoji fora da lista continuam exibidas (TagIconPicker mostra
                value as-is no trigger). */}
            <TagIconPicker value={icone} onChange={setIcone} />
          </div>
          <div>
            <Label htmlFor="tag-form-descricao">Descrição (opcional)</Label>
            <textarea
              id="tag-form-descricao"
              data-testid="tag-form-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={2}
              maxLength={280}
              className="w-full rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 border border-line dark:border-line-dark focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft px-4 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-2 flex items-center gap-2 text-sm">
              <Switch
                checked={escopoGlobal}
                onChange={(e) => setEscopoGlobal(e.target.checked)}
                data-testid="tag-form-global"
              />
              <span className="min-w-0">Vale para todos os fluxos de trabalho</span>
            </label>
            {!escopoGlobal && (
              <div
                data-testid="tag-form-workflows"
                className="ml-7 max-h-40 space-y-1 overflow-y-auto"
              >
                {workflows.map((w) => (
                  <label key={w.slug} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={workflowsPermitidos.includes(w.slug)}
                      onChange={() => toggleWorkflow(w.slug)}
                      data-testid={`tag-form-workflow-${w.slug}`}
                    />
                    <span className="min-w-0 break-words">{w.nome}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Como vai ficar</Label>
            <div className="mt-1">
              <TagBadge
                tag={{
                  id: tag?.id ?? 'preview',
                  nome: nome.trim() || '(sem nome)',
                  cor,
                  icone: icone.trim() || null,
                  ativo: true,
                }}
                size="md"
              />
            </div>
          </div>
          {isEditing && (
            <p
              data-testid="tag-form-usage-count"
              className="text-xs text-ink-3 dark:text-ink-dark-3 italic"
            >
              {usageCount === 1 ? 'Usada em 1 card.' : `Usada em ${usageCount} cards.`}
            </p>
          )}
          {error && (
            <Alert variant="danger" rail className="text-xs">
              {error}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isPending || !nome.trim()}
          data-testid="tag-form-submit"
        >
          {isPending ? 'Salvando…' : isEditing ? 'Salvar' : 'Criar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
